/**
 * DataTable Component - Consolidated Table Component
 * 
 * Replaces: ResponsiveTable, ModernTable, CustomizedTable
 * Uses Material-UI theme system with sx prop (no styled-components)
 * Professional design with new color palette
 * 
 * Features:
 * - Search, sort, pagination
 * - Row selection
 * - Action menu per row
 * - Status badges
 * - Responsive design
 * - Theme-based styling
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  Chip,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Search,
  Download,
  RefreshCw,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

// Status Badge Component
export const StatusBadge = ({ status, label }) => {
  const statusColors = {
    active: 'success',
    inactive: 'default',
    pending: 'warning',
    completed: 'success',
    failed: 'error',
    draft: 'info',
    cancelled: 'error',
    in_progress: 'warning',
  };

  return (
    <Chip
      label={label || status}
      color={statusColors[status?.toLowerCase()] || 'default'}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
        textTransform: 'capitalize',
      }}
    />
  );
};

// Action Menu Component
export const ActionMenu = ({ onView, onEdit, onDelete, rowData }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleAction = (action, event) => {
    event?.stopPropagation();
    handleClose();
    action?.(rowData);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'primary.main',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        }}
      >
        <MoreVertical size={18} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            minWidth: 150,
            boxShadow: 3,
            borderRadius: 2,
          },
        }}
      >
        {onView && (
          <MenuItem onClick={(e) => handleAction(onView, e)}>
            <Eye size={16} style={{ marginRight: 8 }} />
            View
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={(e) => handleAction(onEdit, e)}>
            <Edit size={16} style={{ marginRight: 8 }} />
            Edit
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={(e) => handleAction(onDelete, e)} sx={{ color: 'error.main' }}>
            <Trash2 size={16} style={{ marginRight: 8 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// Main DataTable Component
export const DataTable = ({
  data = [],
  columns = [],
  title,
  subtitle,
  loading = false,
  onSearch,
  onExport,
  onRefresh,
  onRowClick,
  searchPlaceholder = 'Search...',
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25, 50],
  showCheckbox = false,
  actions,
  emptyMessage = 'No data available',
  showSearch = true,
  showPagination = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) =>
      columns.some((column) => {
        const value = row[column.field];
        return value?.toString().toLowerCase().includes(searchLower);
      })
    );
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!orderBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, orderBy, order]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // Handlers
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);
    onSearch?.(value);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(paginatedData.map((row) => row.id));
    } else {
      setSelected([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isSelected = (id) => selected.includes(id);

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: 2,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      {/* Table Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Title Section */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          {title && (
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Search and Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {showSearch && (
            <TextField
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.05),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton
                onClick={onRefresh}
                size="small"
                sx={{
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <RefreshCw size={18} />
              </IconButton>
            </Tooltip>
          )}

          {onExport && (
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                size="small"
                sx={{
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Download size={18} />
              </IconButton>
            </Tooltip>
          )}

          {actions}
        </Box>
      </Box>

      {/* Table Container */}
      <TableContainer
        sx={{
          maxHeight: 600,
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'grey.600',
            },
          },
        }}
      >
        <Table stickyHeader>
          {/* Table Head */}
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
              }}
            >
              {showCheckbox && (
                <TableCell 
                  padding="checkbox"
                  sx={{
                    backgroundColor: 'background.paper',
                    backgroundImage: (theme) => `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  }}
                >
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < paginatedData.length}
                    checked={paginatedData.length > 0 && selected.length === paginatedData.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}

              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || 'left'}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    borderBottom: 2,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    backgroundImage: (theme) => `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    minWidth: column.minWidth || 'auto',
                    width: column.width || 'auto',
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : 'asc'}
                      onClick={() => handleRequestSort(column.field)}
                      sx={{
                        '&:hover': {
                          color: 'primary.main',
                        },
                        '&.Mui-active': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      {column.headerName}
                    </TableSortLabel>
                  ) : (
                    column.headerName
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showCheckbox ? 1 : 0)} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showCheckbox ? 1 : 0)} align="center" sx={{ py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => {
                const isItemSelected = isSelected(row.id);

                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => onRowClick?.(row)}
                    selected={isItemSelected}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                      '&.Mui-selected': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        },
                      },
                    }}
                  >
                    {showCheckbox && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleRowSelect(row.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}

                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {column.renderCell
                          ? column.renderCell({ row, value: row[column.field] })
                          : row[column.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table Pagination */}
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
          }}
        />
      )}
    </Paper>
  );
};

// Default export
export default DataTable;
